import React from 'react';
import { SdmTextButton } from 'components';
import Dialog from '@material-ui/core/Dialog';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { styled } from '@material-ui/core/styles';
import { withStyles } from '@material-ui/styles';
import { TextField } from '@material-ui/core';
import { Controller, useForm } from 'react-hook-form';
import { theme } from 'theme';
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';

const StyledIconButton = styled(IconButton)({
  padding: 5,
});

const CustomFormTextField = withStyles({
  root: {
    width: '95%',
    '& .MuiFormControl-root': {
      width: '95%',
    },
    '& label.Mui-focused': {
      color: `${theme.colors.primary.black}`,
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: `${theme.colors.primary.black}`,
    },
    '& .MuiInput-underline:before': {
      borderBottom: `2px solid ${theme.colors.primary.black}`,
    },
    '& .MuiInputBase-input': {
      fontFamily: 'Parmisan-Regular',
      fontSize: 24,
    },
    '& .MuiFormLabel-root': {
      fontFamily: 'Parmisan-Regular',
      color: `${theme.colors.primary.black}`,
      fontSize: 21,
    },
  },
})(TextField);

const CustomControlLabel = styled(FormControlLabel)({
  '& .MuiRadio-colorSecondary.Mui-checked': {
    color: 'red',
  },
  '& .MuiButtonBase-root input': {
    margin: '0px !important',
    top: '0px !important',
    display: 'none !important',
  },
  '& .MuiButtonBase-root': {
    marginLeft: '10px !important',
  },
  '& .MuiIconButton-colorSecondary:hover': {
    background: 'none',
  },
  '& .MuiTypography-body1': {
    fontFamily: 'Parmisan-Regular',
    marginLeft: '5px',
  },
});

const emailRegex =
  /^(([^<>()\\[\]\\.,;:\s@"]+(\.[^<>()\\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const SettingsDialog = ({
  onClose,
  onAgree,
  i18n,
  isSmDown,
  email,
  prenotazione_appuntamenti,
  isOpen,
}) => {
  const mediaTheme = useTheme();
  const is1024Up = useMediaQuery(mediaTheme.breakpoints.up(1024));
  // Hooks
  const { handleSubmit, errors, control } = useForm({
    defaultValues: {
      email: email || '',
      prenotazione_appuntamenti: prenotazione_appuntamenti || '0',
    },
  });
  // Functions
  const handleOverflowBody = () => {
    const bodyStatus = document.body.getAttribute('style');
    if (bodyStatus.includes('overflow: hidden')) {
      document.body.style.overflow = 'visible';
      document.body.style.paddingTop = 79;
    }
  };

  const handleClose = () => {
    handleOverflowBody();
    onClose && onClose();
  };

  const customSubmit = (data) => {
    handleOverflowBody();
    onAgree && onAgree(data);
  };

  // Renders
  const renderError = (key) => {
    if (errors[key]?.message)
      return (
        <Typography variant="body2-bold" color="primary">
          {errors[key]?.message}
        </Typography>
      );
    return null;
  };
  const radioOptions = [
    { value: '0', label: i18n.prenotazione_appuntamenti.optOne },
    { value: '1', label: i18n.prenotazione_appuntamenti.optTwo },
  ];

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      PaperProps={{
        style: {
          padding: 40,
          ...(is1024Up && {
            width: 742,
          }),
          maxWidth: 742,
          borderRadius: '12px',
        },
      }}
    >
      <div
        tabIndex={0}
        role="button"
        onClick={handleClose}
        style={{
          position: 'absolute',
          top: 25,
          right: 25,
        }}
      >
        <StyledIconButton>
          <CloseIcon fontSize={isSmDown ? 'large' : 'small'} />
        </StyledIconButton>
      </div>
      <form onSubmit={handleSubmit(customSubmit)}>
        <Typography variant="xl-bold">{i18n.title}</Typography>
        <Stack
          flexDirection={'row'}
          alignItems={'center'}
          gap={'10px'}
          marginBottom={'10px'}
          marginTop={'25px'}
        >
          <MailOutlineIcon />
          <Typography variant="body1-bold">{i18n.email.title}</Typography>
        </Stack>
        <Box
          marginBottom={'25px'}
          padding={'25px 15px'}
          style={{ backgroundColor: '#F0F0F0' }}
        >
          <Typography variant="body1">{i18n.email.subOne}</Typography>
          <Typography variant="body1" style={{ marginBottom: '25px' }}>
            [{email}].{i18n.email.subTwo}
          </Typography>
          <Controller
            name={'email'}
            control={control}
            defaultValue={email}
            rules={{
              validate: {
                matchPattern: (v) => {
                  if (!emailRegex.test(v)) {
                    return 'Formato email non valido';
                  }
                },
              },
            }}
            render={(props) => (
              <CustomFormTextField
                onChange={(e) => {
                  props.onChange(e.target.value);
                }}
                value={props.value}
                id={`email`}
                placeholder={i18n.email.placeholder}
              />
            )}
          />
          {renderError('email')}
        </Box>
        <Stack
          flexDirection={'row'}
          alignItems={'center'}
          gap={'10px'}
          marginBottom={'10px'}
        >
          <CalendarTodayOutlinedIcon />
          <Typography variant="body1-bold">
            {i18n.prenotazione_appuntamenti.title}
          </Typography>
        </Stack>
        <Box
          marginBottom={'25px'}
          padding={'25px 15px'}
          style={{ backgroundColor: '#F0F0F0' }}
        >
          <Typography variant="body1" style={{ marginBottom: '25px' }}>
            {i18n.prenotazione_appuntamenti.sub}
          </Typography>
          <Controller
            name={'prenotazione_appuntamenti'}
            control={control}
            rules={{ required: true }}
            defaultValue={prenotazione_appuntamenti}
            render={(props) => {
              return (
                <RadioGroup
                  aria-label="announcements"
                  style={{ display: 'flex', flexDirection: 'row' }}
                  defaultValue={prenotazione_appuntamenti}
                  value={props.value}
                  onChange={(e) => {
                    props.onChange(e.target.value);
                  }}
                >
                  {radioOptions.map((option) => (
                    <CustomControlLabel
                      value={option.value}
                      control={<Radio />}
                      label={option?.label}
                      key={option.label}
                    />
                  ))}
                </RadioGroup>
              );
            }}
          />
          {renderError('owner_flag_privacy_1')}
        </Box>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isSmDown ? 'center' : 'flex-start',
          }}
        >
          <SdmTextButton
            type={'submit'}
            autoFocus
            text={i18n.save}
            height={30}
            style={{ width: 120, height: 44, fontFamily: 'Parmisan-Regular' }}
            variant={'redFill'}
            textVariant={'h4Regular'}
          />
        </div>
      </form>
    </Dialog>
  );
};

export default SettingsDialog;
